import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  useAuth
} from '@clerk/clerk-react';
import Navbar from './components/Navbar';
import { setClerkTokenGetter } from './services/api';
import Dashboard from './pages/Dashboard';
import VoterMap from './pages/VoterMap';
import DataImport from './pages/DataImport';
import Datasets from './pages/Datasets';
import Settings from './pages/Settings';
import CanvasserView from './pages/CanvasserView';
import AdminNavigator from './components/AdminNavigator';

// Check if Clerk is configured
const CLERK_ENABLED = !!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// Component to set up Clerk token for API calls
const ClerkTokenSetup = ({ children }) => {
  const { getToken } = useAuth();

  React.useEffect(() => {
    if (getToken) {
      setClerkTokenGetter(getToken);
    }
  }, [getToken]);

  return children;
};

// Protected route wrapper - only protects if Clerk is enabled
const ProtectedRoute = ({ children }) => {
  if (!CLERK_ENABLED) {
    return children;
  }
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <Navigate to="/sign-in" replace />
      </SignedOut>
    </>
  );
};

// Health check component
const Health = () => {
  React.useEffect(() => {
    document.title = "Election Fabric Health Check";
  }, []);

  return (
    <div style={{
      fontFamily: 'monospace',
      padding: '20px',
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '4px'
    }}>
      <pre>{JSON.stringify({
        status: "healthy",
        service: "election-fabric",
        timestamp: new Date().toISOString(),
        version: "1.0.0"
      }, null, 2)}</pre>
    </div>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Sign-in page component
const SignInPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
    <div className="max-w-md w-full">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Election Fabric</h1>
        <p className="mt-2 text-gray-600">Sign in to access your campaigns</p>
      </div>
      <SignIn
        routing="path"
        path="/sign-in"
        signUpUrl="/sign-up"
        afterSignInUrl="/"
      />
    </div>
  </div>
);

// Sign-up page component
const SignUpPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
    <div className="max-w-md w-full">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Election Fabric</h1>
        <p className="mt-2 text-gray-600">Create your account</p>
      </div>
      <SignUp
        routing="path"
        path="/sign-up"
        signInUrl="/sign-in"
        afterSignUpUrl="/"
      />
    </div>
  </div>
);

function App() {
  // If Clerk is not enabled, render without auth
  if (!CLERK_ENABLED) {
    return (
      <QueryClientProvider client={queryClient}>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <Navbar />
            <main className="container mx-auto px-3 py-4 md:px-4 md:py-8">
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/map" element={<VoterMap />} />
                <Route path="/import" element={<DataImport />} />
                <Route path="/datasets" element={<Datasets />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/canvass/:routeId" element={<CanvasserView />} />
                <Route path="/health" element={<Health />} />
              </Routes>
            </main>
            <AdminNavigator />
          </div>
        </Router>
      </QueryClientProvider>
    );
  }

  // With Clerk enabled, use protected routes and token setup
  return (
    <QueryClientProvider client={queryClient}>
      <ClerkTokenSetup>
        <Router>
          <Routes>
            {/* Public auth routes */}
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />

            {/* Health check - always public */}
            <Route path="/health" element={<Health />} />

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <div className="min-h-screen bg-gray-50">
                    <Navbar />
                    <main className="container mx-auto px-3 py-4 md:px-4 md:py-8">
                      <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/map" element={<VoterMap />} />
                        <Route path="/import" element={<DataImport />} />
                        <Route path="/datasets" element={<Datasets />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/canvass/:routeId" element={<CanvasserView />} />
                      </Routes>
                    </main>
                    <AdminNavigator />
                  </div>
                </ProtectedRoute>
              }
            />
          </Routes>
        </Router>
      </ClerkTokenSetup>
    </QueryClientProvider>
  );
}

export default App;
